import {
  leadsDocumentsListOptions,
  opportunitiesDocumentsListOptions,
  ReturnDocumentReadable
} from '@crm/api'
import { OpportunityContext } from '@crm/contexts/OpportunityContext'
import { DayLabel, TableActions, TableContainer } from '@shared/components'
import { useQueryUpdate } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { downloadFile } from '@shared/utils'
import { useQuery } from '@tanstack/react-query'
import { Flex, MenuProps, TableColumnsType } from 'antd'
import { DocumentDownload, Edit2, Eye, Trash } from 'iconsax-react'
import { useContext, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { AddEditDocument } from './AddEditDocument'
import { DeleteDocument } from './DeleteDocument'
import { DocumentsFilter } from './DocumentsFilter'
import { DocumentsHeader } from './DocumentsHeader'
import { PreviewDocument } from './PreviewDocument'
import { ActionsType } from './types'

type ModalType = 'edit' | 'delete' | 'preview'

const modalTypes: Record<ModalType, React.ComponentType<ActionsType>> = {
  edit: AddEditDocument,
  delete: DeleteDocument,
  preview: PreviewDocument
}

export const DocumentsCol = () => {
  const { t } = useLocales()
  const { id, opportunityId } = useParams()
  const [modalState, setModalState] = useState<{
    type: ModalType | null
    document: ReturnDocumentReadable | null
  }>({ document: null, type: null })
  const { type } = useContext(OpportunityContext)

  const { params } = useQueryUpdate()

  const { data: leadData, isLoading: isLeadLoading } = useQuery({
    ...leadsDocumentsListOptions({
      path: { lead_id: String(id) },
      query: { ...params }
    }),
    enabled: !!id && type === 'lead'
  })

  const { data: opportunityData, isLoading: isOpportunityLoading } = useQuery({
    ...opportunitiesDocumentsListOptions({
      path: { opportunity_id: String(opportunityId) },
      query: { ...params }
    }),
    enabled: !!opportunityId && type === 'opportunity'
  })

  const data = leadData || opportunityData
  const isLoading = isLeadLoading || isOpportunityLoading

  const actionItems = (
    document: ReturnDocumentReadable
  ): MenuProps['items'] => [
    {
      label: t('generic.preview'),
      key: 'preview',
      icon: <Eye size={20} />,
      onClick: () => setModalState({ document, type: 'preview' })
    },
    {
      label: t('buttons.edit'),
      key: 'edit',
      icon: <Edit2 size={20} />,
      onClick: () => setModalState({ document, type: 'edit' })
    },
    {
      label: t('documents.download'),
      key: 'download',
      icon: <DocumentDownload size={20} />,
      onClick: () =>
        downloadFile(
          { file: { url: document?.attachment?.file || '', headers: {} } },
          String(document?.name || document?.attachment?.title)
        )
    },
    {
      type: 'divider'
    },
    {
      label: t('buttons.delete'),
      key: 'delete',
      danger: true,
      icon: <Trash size={20} />,
      onClick: () => setModalState({ document, type: 'delete' })
    }
  ]

  const columns: TableColumnsType<ReturnDocumentReadable> = [
    {
      title: t('generic.name'),
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: t('filter.category'),
      dataIndex: ['category', 'name'],
      key: 'category_name'
    },
    {
      title: t('filter.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (createdAt) => <DayLabel date={createdAt} />
    },
    {
      width: 50,
      key: 'actions',
      fixed: 'right',
      render: (_, document) => <TableActions options={actionItems(document)} />
    }
  ]

  const actionComponent = useMemo(() => {
    const Component = modalState.type ? modalTypes[modalState.type] : null

    return Component && modalState.document ? (
      <Component
        open
        document={modalState.document}
        onCancel={() => setModalState({ document: null, type: null })}
      />
    ) : null
  }, [modalState])

  return (
    <Flex vertical gap={16}>
      <TableContainer
        columns={columns}
        data={data}
        loading={isLoading}
        leftChildren={<DocumentsFilter />}
        rightChildren={<DocumentsHeader />}
        rowKey="id"
      />

      {actionComponent}
    </Flex>
  )
}
